const chapter = {
  id: 'respiration-plants',
  slug: 'respiration-plants',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Respiration in Plants',
        hindi: 'पादपों में श्वसन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3 questions in NEET',
        hindi: 'NEET में 3 प्रश्न दो व्यापक प्रकार: **मेरिस्टेमेटिक** (विभाजन) + **स्थायी** (कोई विभाजन नहीं)। **मेरिस्टेम्स**:',
  },
  syllabusTags: ['Plant Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'overview',
      title: {
        english: 'Cellular Respiration — Overview',
        hindi: 'कोशिकीय श्वसन — अवलोकन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Stepwise oxidation of complex compounds → CO₂ + H₂O + **chemical energy (ATP)**.',
        hindi: 'जटिल यौगिकों का चरणबद्ध ऑक्सीकरण → CO₂ + H₂O + **रासायनिक ऊर्जा (ATP)**। **श्वसन सब्सट्रेट** - ग्लूकोज (सबसे आम), वसा, प्रोटीन, कार्बनिक अम्ल।',
            },
            {
              english: '**Respiratory substrates** — glucose (most common), fats, proteins, organic acids.',
        hindi: '**श्वसन सब्सट्रेट** - ग्लूकोज (सबसे आम), वसा, प्रोटीन, कार्बनिक अम्ल।',
            },
            {
              english: 'Site — **cytosol (glycolysis)** + **mitochondria (Krebs + ETC)**.',
        hindi: 'साइट - **साइटोसोल (ग्लाइकोलाइसिस)** + **माइटोकॉन्ड्रिया (क्रेब्स + ईटीसी)**।',
            },
            {
              english: 'In plants: no specialised respiratory organs; gas exchange through **stomata, lenticels, root hairs**.',
        hindi: 'पौधों में: कोई विशेष श्वसन अंग नहीं; **स्टोमेटा, लेंटिसल्स, जड़ बाल** के माध्यम से गैस विनिमय।',
            },
            {
              english: 'Overall:',
        hindi: 'कुल मिलाकर:',
            },
          ],
        },
        {
          type: 'equation',
          latex: 'C_6H_{12}O_6 + 6\\,O_2 \\rightarrow 6\\,CO_2 + 6\\,H_2O + 686\\,\\text{kcal}',
        },
      ],
    },
    {
      id: 'glycolysis',
      title: {
        english: 'Glycolysis (EMP Pathway)',
        hindi: 'ग्लाइकोलाइसिस (EMP मार्ग)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Discovered by **Embden, Meyerhof & Parnas** (EMP). Site = **cytosol**. Common to aerobic + anaerobic.',
        hindi: '**एम्बडेन, मेयरहोफ़ और पारनास** (ईएमपी) द्वारा खोजा गया। Site = **cytosol**. एरोबिक + एनारोबिक के लिए सामान्य।',
            },
            {
              english: 'Glucose (6C) → 2 × **Pyruvate (3C)** in 10 steps.',
        hindi: 'ग्लूकोज (6C) → 2 × **पाइरूवेट (3C)** 10 चरणों में।',
            },
            {
              english: 'Net per glucose: **2 ATP + 2 NADH + 2 Pyruvate**.',
        hindi: 'प्रति ग्लूकोज शुद्ध: **2 एटीपी + 2 एनएडीएच + 2 पाइरूवेट**।',
            },
            {
              english: '(Investment: 2 ATP used in steps 1 & 3; payoff: 4 ATP + 2 NADH produced).',
        hindi: '(निवेश: चरण 1 और 3 में प्रयुक्त 2 एटीपी; भुगतान: 4 एटीपी + 2 एनएडीएच उत्पादित)।',
            },
          ],
        },
        {
          type: 'equation',
          latex: 'C_6H_{12}O_6 + 2\\,NAD^+ + 2\\,ADP + 2\\,P_i \\rightarrow 2\\,Pyruvate + 2\\,NADH + 2\\,ATP',
        },
      ],
    },
    {
      id: 'fermentation',
      title: {
        english: 'Fermentation (Anaerobic)',
        hindi: 'किण्वन (अवायवीय)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'In absence of O₂: pyruvate is reduced (regenerate NAD⁺ for glycolysis to continue).',
        hindi: 'O₂ की अनुपस्थिति में: पाइरूवेट कम हो जाता है (ग्लाइकोलाइसिस जारी रखने के लिए NAD⁺ को पुन: उत्पन्न करता है)।',
            },
            {
              english: '**Alcoholic fermentation** (yeast):',
        hindi: '**अल्कोहलिक किण्वन** (खमीर):',
            },
          ],
        },
        {
          type: 'equation',
          latex: '\\text{Pyruvate} \\xrightarrow{\\text{pyruvate decarboxylase}} \\text{Acetaldehyde} + CO_2 \\xrightarrow{\\text{ADH}} \\text{Ethanol}',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Lactic acid fermentation** (animal muscle, *Lactobacillus*) — pyruvate → lactate (no CO₂).',
        hindi: '**लैक्टिक एसिड किण्वन** (पशु की मांसपेशी, *लैक्टोबैसिलस*) - पाइरूवेट → लैक्टेट (कोई CO₂ नहीं)।',
            },
            {
              english: 'Yields only **2 ATP / glucose** + heat → very inefficient (< 7 % of energy released).',
        hindi: 'केवल **2 एटीपी/ग्लूकोज** + ऊष्मा उत्पन्न करता है → बहुत अकुशल (<7% ऊर्जा जारी)।',
            },
            {
              english: 'Yeast cannot tolerate > 13 % alcohol → distillation needed for higher %.',
        hindi: 'यीस्ट > 13% अल्कोहल बर्दाश्त नहीं कर सकता → अधिक % के लिए आसवन की आवश्यकता होती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'aerobic-respiration',
      title: {
        english: 'Aerobic Respiration — Link Reaction & Krebs',
        hindi: 'वायवीय श्वसन — संयोजक अभिक्रिया और क्रेब्स चक्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Link reaction** (in mitochondrial **matrix**) — pyruvate (3C) → **Acetyl-CoA (2C)** + CO₂ + NADH; catalysed by **pyruvate dehydrogenase complex** (needs Mg²⁺, CoA, NAD⁺).',
        hindi: '**लिंक प्रतिक्रिया** (माइटोकॉन्ड्रियल में **आव्यूह**) - पाइरूवेट (3C) → **एसिटाइल-सीओए (2C)** + CO₂ + NADH; **पाइरूवेट डिहाइड्रोजनेज कॉम्प्लेक्स** द्वारा उत्प्रेरित (Mg²⁺, CoA, NAD⁺ की आवश्यकता है)।',
            },
            {
              english: 'Per glucose (2 pyruvate): **2 NADH + 2 CO₂**.',
        hindi: 'प्रति ग्लूकोज (2 पाइरूवेट): **2 NADH + 2 CO₂**।',
            },
            {
              english: '**Krebs / TCA / Citric Acid Cycle** — Hans Krebs (1937, Nobel). Site = **mitochondrial matrix**.',
        hindi: '**क्रेब्स / टीसीए / साइट्रिक एसिड चक्र** - हंस क्रेब्स (1937, नोबेल)। Site = **mitochondrial आव्यूह**.',
            },
            {
              english: '• Acetyl-CoA + OAA (4C) → **Citrate (6C)** by citrate synthase.',
        hindi: '• एसिटाइल-सीओए + ओएए (4सी) → **साइट्रेट (6सी)** साइट्रेट सिंथेज़ द्वारा।',
            },
            {
              english: '• Cycle goes through isocitrate → α-KG (5C) → succinyl-CoA → succinate (4C) → fumarate → malate → OAA.',
        hindi: '• चक्र आइसोसाइट्रेट → α-KG (5C) → succinyl-CoA → सक्सिनेट (4C) → फ्यूमरेट → मैलेट → OAA से होकर गुजरता है।',
            },
            {
              english: 'Per Acetyl-CoA: **3 NADH + 1 FADH₂ + 1 GTP (≈ATP) + 2 CO₂**.',
        hindi: 'प्रति एसिटाइल-सीओए: **3 NADH + 1 FADH₂ + 1 GTP (≈ATP) + 2 CO₂**।',
            },
            {
              english: 'Per glucose (2 turns): **6 NADH + 2 FADH₂ + 2 ATP + 4 CO₂**.',
        hindi: 'प्रति ग्लूकोज (2 मोड़): **6 एनएडीएच + 2 एफएडीएच₂ + 2 एटीपी + 4 सीओ₂**।',
            },
          ],
        },
      ],
    },
    {
      id: 'etc-osphos',
      title: {
        english: 'ETC & Oxidative Phosphorylation',
        hindi: 'इलेक्ट्रॉन परिवहन श्रृंखला और ऑक्सीकृत फॉस्फोरिलेशन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Site = **inner mitochondrial membrane** (cristae).',
        hindi: 'साइट = **आंतरिक माइटोकॉन्ड्रियल झिल्ली** (क्रिस्टे)।',
            },
            {
              english: 'Complexes: **I (NADH dehydrogenase) → CoQ → III (Cyt b-c₁) → Cyt c → IV (Cyt a-a₃)** → O₂ → H₂O. **Complex II (succinate DH)** feeds FADH₂ into CoQ.',
        hindi: 'कॉम्प्लेक्स: **I (NADH डिहाइड्रोजनेज) → CoQ → III (Cyt b-c₁) → Cyt c → IV (Cyt a-a₃)** → O₂ → H₂O। **कॉम्प्लेक्स II (सक्सिनेट डीएच)** FADH₂ को CoQ में फीड करता है।',
            },
            {
              english: 'H⁺ pumped into intermembrane space → proton motive force → flow back through **Complex V (F₀-F₁ ATP synthase)** → ATP.',
        hindi: 'H⁺ को इंटरमेम्ब्रेन स्पेस में पंप किया जाता है → प्रोटॉन प्रेरक बल → **कॉम्प्लेक्स V (F₀-F₁ ATP सिंथेज़)** → ATP के माध्यम से वापस प्रवाहित होता है।',
            },
            {
              english: '**Chemiosmotic hypothesis** (Peter Mitchell, Nobel 1978).',
        hindi: '**केमियोस्मोटिक परिकल्पना** (पीटर मिशेल, नोबेल 1978)।',
            },
            {
              english: 'Yield: **1 NADH ≈ 3 ATP**, **1 FADH₂ ≈ 2 ATP** (theoretical).',
        hindi: 'उपज: **1 एनएडीएच ≈ 3 एटीपी**, **1 एफएडीएच₂ ≈ 2 एटीपी** (सैद्धांतिक)।',
            },
            {
              english: 'O₂ acts as **terminal electron acceptor** at Complex IV; without O₂, ETC stops → no ATP from oxidative phosphorylation.',
        hindi: 'O₂ कॉम्प्लेक्स IV में **टर्मिनल इलेक्ट्रॉन स्वीकर्ता** के रूप में कार्य करता है; O₂ के बिना, ETC बंद हो जाता है → ऑक्सीडेटिव फास्फारिलीकरण से कोई ATP नहीं। श्वसन **उभयचर** (कैटाबोलिक + एनाबॉलिक) है - ग्लाइकोलाइसिस और क्रेब्स के मध्यवर्ती जैवसंश्लेषण (एमिनो एसिड, न्यूक्लियोटाइड, फैटी एसिड) के अग्रदूत हैं।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Stage', 'Site', 'ATP', 'NADH', 'FADH₂', 'CO₂'],
          rows: [
            ['Glycolysis', 'Cytosol', '2', '2', '0', '0'],
            ['Link reaction', 'Matrix', '0', '2', '0', '2'],
            ['Krebs (×2)', 'Matrix', '2', '6', '2', '4'],
            ['ETC', 'Inner mito mem', '34 (from 10 NADH + 2 FADH₂)', '—', '—', '0'],
            ['**Total**', '—', '**38** (or 36 in animal cells)', '—', '—', '6'],
          ],
        },
      ],
    },
    {
      id: 'amphibolic',
      title: {
        english: 'Amphibolic Pathway, RQ & Variants',
        hindi: 'उभयचर मार्ग, श्वसन गुणांक और विविधताएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Respiration is **amphibolic** (catabolic + anabolic) — intermediates of glycolysis & Krebs are precursors for biosynthesis (amino acids, nucleotides, fatty acids).',
        hindi: 'श्वसन **उभयचर** (कैटाबोलिक + एनाबॉलिक) है - ग्लाइकोलाइसिस और क्रेब्स के मध्यवर्ती जैवसंश्लेषण (एमिनो एसिड, न्यूक्लियोटाइड, फैटी एसिड) के अग्रदूत हैं।',
            },
            {
              english: '**Respiratory Quotient (RQ)** = volume of CO₂ evolved / volume of O₂ consumed.',
        hindi: '**श्वसन भागफल (आरक्यू)** = विकसित CO₂ की मात्रा / उपभोग की गई O₂ की मात्रा।',
            },
          ],
        },
        {
          type: 'equation',
          latex: 'RQ = \\dfrac{V_{CO_2}}{V_{O_2}}',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Carbohydrates: RQ = 1.0** (glucose).',
        hindi: '**कार्बोहाइड्रेट: RQ = 1.0** (ग्लूकोज)।',
            },
            {
              english: '**Fats: RQ ≈ 0.7** (tripalmitin = 0.7).',
        hindi: '**वसा: आरक्यू ≈ 0.7** (ट्रिपलमिटिन = 0.7)।',
            },
            {
              english: '**Proteins: RQ ≈ 0.9**.',
        hindi: '**प्रोटीन: RQ ≈ 0.9**।',
            },
            {
              english: '**Organic acids: RQ > 1** (oxalic acid = 4.0, malic acid = 1.33).',
        hindi: '**कार्बनिक अम्ल: RQ > 1** (ऑक्सालिक acid = 4.0, मैलिक acid = 1.33)।',
            },
            {
              english: 'Anaerobic respiration: **RQ = ∞** (CO₂ released, no O₂ consumed).',
        hindi: 'अवायवीय श्वसन: **RQ = ∞** (CO₂ released, कोई O₂ का सेवन नहीं)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'C_6H_{12}O_6 + 6\\,O_2 \\rightarrow 6\\,CO_2 + 6\\,H_2O + 686 \\text{ kcal}',
      desc: {
        english: '',
        hindi: 'C 6H 12 O 6 + 6\\,O 2 6\\,CO 2 + 6\\,H 2O + 686 किलो कैलोरी',
      },
    },
    {
      latex: '\\text{Glycolysis: 2 ATP + 2 NADH (cytosol)}',
      desc: {
        english: '',
        hindi: 'ग्लाइकोलाइसिस: 2 एटीपी + 2 एनएडीएच (साइटोसोल)',
      },
    },
    {
      latex: '\\text{Krebs (per glucose): 2 ATP + 6 NADH + 2 FADH}_2 + 4 \\,CO_2',
      desc: {
        english: '',
        hindi: 'क्रेब्स (प्रति ग्लूकोज): 2 एटीपी + 6 एनएडीएच + 2 एफएडीएच 2 + 4 \\,सीओ 2',
      },
    },
    {
      latex: '\\text{Total per glucose} = 38 \\text{ ATP (plant)}, 36 \\text{ ATP (animal)}',
      desc: {
        english: '',
        hindi: 'कुल प्रति ग्लूकोज = 38 एटीपी (पौधा), 36 एटीपी (पशु)',
      },
    },
    {
      latex: '\\text{1 NADH} \\approx 3 \\text{ ATP}; \\, 1 \\text{ FADH}_2 \\approx 2 \\text{ ATP}',
      desc: {
        english: '',
        hindi: '1 एनएडीएच 3 एटीपी; \\, 1 एफएडीएच 2 2 एटीपी',
      },
    },
    {
      latex: 'RQ = V_{CO_2} / V_{O_2}',
      desc: {
        english: 'CHO=1, fats≈0.7, proteins≈0.9',
        hindi: 'CHO=1, वसा≈0.7, प्रोटीन≈0.9',
      },
    },
    {
      latex: '\\text{Fermentation: 2 ATP only; ethanol/lactate}',
      desc: {
        english: '',
        hindi: 'किण्वन: केवल 2 एटीपी; इथेनॉल/लैक्टेट',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Yield',
      problem: {
        english: 'Net ATP yield from complete oxidation of one glucose in plants is:',
        hindi: 'पौधों में एक ग्लूकोज के पूर्ण ऑक्सीकरण से शुद्ध एटीपी उपज है:',
      },
      answer: {
        english: '38 ATP',
        hindi: '38 एटीपी',
      },
      solution: {
        english: '2 (glycolysis) + 2 (Krebs) + 34 (ETC).',
        hindi: '2 (ग्लाइकोलाइसिस) + 2 (क्रेब्स) + 34 (ईटीसी)।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'RQ',
      problem: {
        english: 'RQ for fats is approximately:',
        hindi: 'वसा के लिए RQ लगभग है:',
      },
      answer: {
        english: '0.7',
        hindi: '0.7',
      },
      solution: {
        english: 'Tripalmitin RQ = 0.7.',
        hindi: 'त्रिपलमिटिन RQ = 0.7.',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Site',
      problem: {
        english: 'Krebs cycle takes place in:',
        hindi: 'क्रेब्स चक्र होता है:',
      },
      answer: {
        english: 'Mitochondrial matrix',
        hindi: 'माइटोकॉन्ड्रियल आव्यूह',
      },
      solution: {
        english: 'ETC on inner membrane; glycolysis in cytosol.',
        hindi: 'आंतरिक झिल्ली पर ईटीसी; साइटोसोल में ग्लाइकोलाइसिस।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Acceptor',
      problem: {
        english: 'Terminal electron acceptor in aerobic respiration is:',
        hindi: 'एरोबिक श्वसन में टर्मिनल इलेक्ट्रॉन स्वीकर्ता है:',
      },
      answer: {
        english: 'Oxygen (at Complex IV)',
        hindi: 'ऑक्सीजन (कॉम्प्लेक्स IV पर)',
      },
      solution: {
        english: 'Reduced to H₂O.',
        hindi: 'H₂O तक कम हो गया।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Fermentation',
      problem: {
        english: 'In alcoholic fermentation, end products are:',
        hindi: 'अल्कोहलिक किण्वन में, अंतिम उत्पाद हैं:',
      },
      answer: {
        english: 'Ethanol + CO₂',
        hindi: 'इथेनॉल + CO₂',
      },
      solution: {
        english: 'Catalysed by pyruvate decarboxylase + ADH in yeast.',
        hindi: 'यीस्ट में पाइरूवेट डिकार्बोक्सिलेज़ + ADH द्वारा उत्प्रेरित।',
      },
    },
  ],
  next: {
    slug: 'plant-growth-development',
    title: 'Plant Growth and Development',
  },
};

export default chapter;
